import { create } from 'zustand';
import { 
  massPaymentApiService, 
  MassPaymentUploadRequest, 
  MassPaymentProcessRequest 
} from '../services/api/masspayments';
import { 
  UploadResponse,
  GetCurrentProcessedTransactionsRequest, 
  TransactionsFinalLogsRequest, 
  MassPaymentHistoryItem 
} from '../services/api/masspayments/types';
import { Transaction, LogEntry } from '../Pages/MassPayments/types';
import { utils } from '../utils/utils';
import { merchantApiService } from '../services/api/merchants/MerchantApiService';
import { isSuperAdmin } from '../authGuard';

const NETWORK_TYPES = {
  DEFAULT: 'BISERA',
  BULK: 'BISERA',
  INST: 'INST'
} as const;

const POLLING_INTERVAL_MS = 1000;
const FILE_STATUS_POLL_INTERVAL_MS = 1000;

type Step = 'upload' | 'uploading' | 'preview' | 'execution' | 'executing' | 'processing' | 'logs';
type Tab = 'upload' | 'history';
interface MassPaymentsState {
  currentStep: Step;
  activeTab: Tab;
  selectedFile: File | null;
  validationResult: string | any;
  isUploading: boolean;
  uploadProgress: number;
  uploadResponse: UploadResponse | null;
  fileId: string;
  transactions: Transaction[];
  totalAmount: number;
  selectedChannel: string;
  selectedMerchant: any;
  selectedHistoryMerchant: any;
  merchants: any[];
  isProcessing: boolean;
  executionProgress: number;
  processingTransactions: any[];
  isPolling: boolean;
  pollingInterval: NodeJS.Timeout | null;
  fileStatusPollingInterval: NodeJS.Timeout | null;
  logs: LogEntry[];
  searchTerm: string;
  isExporting: boolean;
  exportError: string | null;
  historyItems: MassPaymentHistoryItem[];
  isLoadingHistory: boolean;
  selectedHistoryItem: MassPaymentHistoryItem | null;
  fileHistory: any[];
}

interface MassPaymentsActions {
  setSelectedFile: (file: File | null) => void;
  uploadFile: () => Promise<void>;
  pollFileStatus: (fileId: number) => Promise<string | null>;
  setSelectedChannel: (channel: string) => void;
  setSelectedMerchant: (merchant: any) => void;
  setSelectedHistoryMerchant: (merchant: any) => void;
  setCurrentStep: (step: Step) => void;
  setActiveTab: (tab: Tab) => void;
  processTransactions: () => Promise<void>;
  startPollingProcessedTransactions: () => void;
  stopPolling: () => void;
  getFinalLogs: () => Promise<void>;
  exportLogs: () => Promise<void>;
  downloadLogsForFile: (fileId: number, status?: string) => Promise<void>;
  setSearchTerm: (term: string) => void;
  clearExportError: () => void;
  loadHistory: () => Promise<void>;
  setSelectedHistoryItem: (item: MassPaymentHistoryItem | null) => void;
  downloadErrorLog: (fileId: number) => Promise<void>;
  loadMerchants: () => Promise<void>;
  cancel: () => void;
  startNew: () => void;
  reset: () => void;
}

const createResetState = (): Partial<MassPaymentsState> => ({
  currentStep: 'upload',
  selectedFile: null,
  validationResult: '',
  transactions: [],
  totalAmount: 0,
  isUploading: false,
  uploadProgress: 0,
  executionProgress: 0,
  searchTerm: '',
  logs: [],
  processingTransactions: [],
  fileId: '',
  uploadResponse: null,
  isExporting: false
});

const isTerminalStatus = (status: string): boolean => {
  return status === 'Uploaded' || status.includes('Failed');
};

const initialState: MassPaymentsState = {
  currentStep: 'upload',
  activeTab: 'upload',
  selectedFile: null,
  validationResult: '',
  isUploading: false,
  uploadProgress: 0,
  uploadResponse: null,
  fileId: '',
  transactions: [],
  totalAmount: 0,
  selectedChannel: NETWORK_TYPES.BULK,
  selectedMerchant: null,
  selectedHistoryMerchant: null,
  merchants: [],
  executionProgress: 0,
  logs: [],
  processingTransactions: [],
  fileHistory: [],
  searchTerm: '',
  isPolling: false,
  pollingInterval: null,
  fileStatusPollingInterval: null,
  isProcessing: false,
  isExporting: false,
  exportError: null,
  historyItems: [],
  isLoadingHistory: false,
  selectedHistoryItem: null,
};

export const useMassPaymentsStore = create<MassPaymentsState & MassPaymentsActions>((set, get) => ({
  ...initialState,

  setSelectedFile: (file) => {
    set({ selectedFile: file, validationResult: '' });
  },

  uploadFile: async () => {
    const { selectedFile, selectedChannel, selectedMerchant } = get();
    
    if (!selectedFile) {
      set({ validationResult: 'Please select a file' });
      return;
    }

    if (!selectedChannel) {
      set({ validationResult: 'Please select a channel' });
      return;
    }

    if (isSuperAdmin() && (!selectedMerchant || selectedMerchant === 0)) {
      set({ validationResult: 'Please select a merchant' });
      return;
    }

    set({ 
      currentStep: 'uploading',
      isUploading: true, 
      uploadProgress: 10,
      validationResult: ''
    });

    try {
      const fileContent = await utils.fileToBase64(selectedFile);
      const merchantId = utils.resolveMerchantId(selectedMerchant, isSuperAdmin());

      const uploadPayload: MassPaymentUploadRequest = {
        fileName: selectedFile.name,
        fileContent,
        merchantId,
        networkType: selectedChannel
      };

      const uploadResult = await massPaymentApiService.uploadMassPaymentFile(uploadPayload);
      
      if (!uploadResult.success || !uploadResult.data) {
        set({
          currentStep: 'upload',
          validationResult: utils.parseErrorMessage(uploadResult),
          isUploading: false,
          uploadProgress: 0,
          selectedFile: null
        });
        return;
      }

      const { fileId } = uploadResult.data;
      set({ 
        fileId: fileId.toString(),
        uploadProgress: 30
      });

      const finalStatus = await get().pollFileStatus(fileId);
      
      if (!finalStatus) {
        set({
          currentStep: 'upload',
          validationResult: 'Failed to get file status. Please try again.',
          isUploading: false,
          uploadProgress: 0,
          selectedFile: null
        });
        return;
      }

      set({ uploadProgress: 60 });

      const responseResult = await massPaymentApiService.getUploadResponse(fileId);
      
      if (!responseResult.success || !responseResult.data) {
        const errorMessage = responseResult.message?.includes('not found') || 
                           responseResult.message?.includes('Not Found')
          ? 'Something went wrong! Please try uploading the file again.'
          : utils.parseErrorMessage(responseResult);
        
        set({
          currentStep: 'upload',
          validationResult: errorMessage,
          isUploading: false,
          uploadProgress: 0,
          selectedFile: null
        });
        return;
      }

      const response = responseResult.data;
      set({ 
        uploadResponse: response,
        uploadProgress: 80
      });

      if (response.globalErrors?.length > 0 || response.responseCode !== 200) {
        set({
          currentStep: 'upload',
          validationResult: response,
          isUploading: false,
          uploadProgress: 0,
          selectedFile: null
        });
        return;
      }

      if (response.fileStatus === 'Uploaded') {
        const transactions = utils.mapRecordsToTransactions(
          response.transactions || [],
          response.recordValidations || []
        );

        set({
          currentStep: 'preview',
          transactions,
          totalAmount: response.totalAmount,
          isUploading: false,
          uploadProgress: 100
        });
      } else {
        set({
          currentStep: 'upload',
          validationResult: `File upload failed. Status: ${response.fileStatus}`,
          isUploading: false,
          uploadProgress: 0,
          selectedFile: null
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      set({
        currentStep: 'upload',
        validationResult: 'Error uploading file. Please try again.',
        isUploading: false,
        uploadProgress: 0,
        selectedFile: null
      });
    }
  },

  pollFileStatus: async (fileId: number): Promise<string | null> => {
    return new Promise((resolve) => {
      const poll = async () => {
        try {
          const statusResult = await massPaymentApiService.getFileStatus(fileId);
          
          if (statusResult.success && statusResult.data) {
            const { status } = statusResult.data;
            
            if (isTerminalStatus(status)) {
              const { fileStatusPollingInterval } = get();
              if (fileStatusPollingInterval) {
                clearTimeout(fileStatusPollingInterval);
                set({ fileStatusPollingInterval: null });
              }
              resolve(status);
              return;
            }
            
            const interval = setTimeout(poll, FILE_STATUS_POLL_INTERVAL_MS);
            set({ fileStatusPollingInterval: interval });
          } else {
            resolve(null);
          }
        } catch (error) {
          console.error('Error polling file status:', error);
          resolve(null);
        }
      };
      
      poll();
    });
  },

  setSelectedChannel: (channel) => set({ selectedChannel: channel }),
  setSelectedMerchant: (merchant) => set({ selectedMerchant: merchant }),
  setSelectedHistoryMerchant: (merchant) => set({ selectedHistoryMerchant: merchant }),

  setCurrentStep: (step) => set({ currentStep: step }),
  setActiveTab: (tab) => set({ activeTab: tab }),

  processTransactions: async () => {
    const { fileId, selectedChannel } = get();
    
    set({ 
      currentStep: 'executing', 
      executionProgress: 0,
      logs: [],
      processingTransactions: [],
      isProcessing: true
    });
    
    try {
      const processRequest: MassPaymentProcessRequest = {
        fileId: parseInt(fileId),
        networkType: selectedChannel
      };
      
      const result = await massPaymentApiService.processTransactions(processRequest);
      
      if (result.success) {
        get().startPollingProcessedTransactions();
      } else {
        set({
          currentStep: 'preview',
          validationResult: utils.parseErrorMessage(result),
          isProcessing: false
        });
      }
    } catch (error) {
      console.error('Error processing transactions:', error);
      set({
        currentStep: 'preview',
        validationResult: error instanceof Error ? error.message : 'Error processing transactions. Please try again.',
        isProcessing: false
      });
    }
  },

  startPollingProcessedTransactions: () => {
    const state = get();
    if (state.isPolling) return;
    
    set({ isPolling: true });
    
    let lastTransactionId = 0;
    let processedTransactions: any[] = [];
    const expectedCount = state.uploadResponse?.recordCount || 0;
    
    const pollTransactions = async () => {
      try {
        const request: GetCurrentProcessedTransactionsRequest = {
          fileId: parseInt(state.fileId),
          lastTransactionId
        };
        
        const result = await massPaymentApiService.getCurrentProcessedTransactions(request);
        
        if (result.success && result.data) {
          const newTransactions = result.data.transactions || [];
          processedTransactions = [...processedTransactions, ...newTransactions];
          
          const newProcessingTransactions = newTransactions.map((tx: any) => ({
            id: tx.id,
            status: tx.status === 'Processed' ? 'Success' : 'Failed',
            referenceNo: tx.referenceNo || '-'
          }));
          
          set(prev => ({
            processingTransactions: [...prev.processingTransactions, ...newProcessingTransactions],
            executionProgress: Math.min((processedTransactions.length / expectedCount) * 100, 100)
          }));
          
          if (newTransactions.length > 0) {
            lastTransactionId = Math.max(...newTransactions.map((tx: any) => tx.id));
          }
          
          if (processedTransactions.length > 0 && get().currentStep === 'executing') {
            set({ currentStep: 'processing' });
          }
          
          if (processedTransactions.length >= expectedCount) {
            console.log('All transactions processed. Stopping polling.');
            set({
              currentStep: 'logs',
              executionProgress: 100,
              isPolling: false,
              isProcessing: false
            });
            get().getFinalLogs();
            return;
          }
        }
        
        const interval = setTimeout(pollTransactions, POLLING_INTERVAL_MS);
        set({ pollingInterval: interval });
      } catch (error) {
        console.error('Error polling processed transactions:', error);
        const interval = setTimeout(pollTransactions, POLLING_INTERVAL_MS);
        set({ pollingInterval: interval });
      }
    };
    
    pollTransactions();
  },

  stopPolling: () => {
    const { pollingInterval, fileStatusPollingInterval } = get();
    
    if (pollingInterval) {
      clearTimeout(pollingInterval);
    }
    
    if (fileStatusPollingInterval) {
      clearTimeout(fileStatusPollingInterval);
    }
    
    set({ 
      isPolling: false, 
      pollingInterval: null,
      fileStatusPollingInterval: null
    });
  },

  getFinalLogs: async () => {
    const { fileId } = get();
    
    try {
      const request: TransactionsFinalLogsRequest = {
        fileId: parseInt(fileId)
      };
      
      const result = await massPaymentApiService.getTransactionsFinalLogs(request);
      
      if (result.success && result.data) {
        const finalLogs = result.data.transactionLogs.map((log: any) => {
          // Parse JSON strings safely
          let requestToFlexcube = null;
          let flexcubeResponse = null;
          
          try {
            if (log.requestToFlexcube) {
              requestToFlexcube = typeof log.requestToFlexcube === 'string' 
                ? JSON.parse(log.requestToFlexcube) 
                : log.requestToFlexcube;
            }
          } catch (e) {
            console.warn('Failed to parse requestToFlexcube:', e);
          }
          
          try {
            if (log.flexcubeResponse) {
              flexcubeResponse = typeof log.flexcubeResponse === 'string' 
                ? JSON.parse(log.flexcubeResponse) 
                : log.flexcubeResponse;
            }
          } catch (e) {
            console.warn('Failed to parse flexcubeResponse:', e);
          }
          
          // Create a JSON object with all relevant transaction data
          const detailsJson = {
            file_id: parseInt(fileId),
            record_id: log.recordId,
            id: log.id,
            purpose: log.purpose,
            status: log.status,
            error: log.error || null,
            flexcubeReference: log.flexcubeReference || null,
            recipientName: log.recipientName || null,
            recipientIBAN: log.recipientIBAN || null,
            recipientBIC: log.recipientBIC || null,
            amount: log.amount || null,
            requestToFlexcube,
            flexcubeResponse
          };
          
          return {
            timestamp: log.uploadTimestamp || new Date().toISOString(),
            fileId,
            id: log.id,
            recordId: log.recordId,
            action: log.purpose || `Transaction ${log.recordId}`,
            details: JSON.stringify(detailsJson),
            status: log.status === 'Processed' ? 'Success' : 'Failed',
            referenceNo: log.flexcubeReference || '',
            purpose: log.purpose,
            error: log.error,
            flexcubeReference: log.flexcubeReference
          };
        });
        
        set({ logs: finalLogs });
      }
    } catch (error) {
      console.error('Error getting final logs:', error);
    }
  },

  exportLogs: async () => {
    const { fileId } = get();
    await get().downloadLogsForFile(parseInt(fileId), 'Processed');
  },

  downloadLogsForFile: async (fileId: number, status?: string) => {
    try {
      set({ isExporting: true, exportError: null });
      
      if (status === 'Processed') {
        const result = await massPaymentApiService.downloadFinalLogs({ fileId });
        
        if (result.success && result.data) {
          utils.downloadFileFromBase64(result.data.contentBase64, `mass-payment-logs-${fileId}.csv`);
        } else {
          set({ exportError: result.message || 'Failed to download logs. Please try again.' });
          console.error('Failed to download logs:', result.message);
        }
      } else {
        const result = await massPaymentApiService.downloadProcessingErrorLog({ fileId });
        
        if (result.success && result.data) {
          utils.downloadFileFromBase64(result.data.contentBase64, `error-log-${fileId}.txt`);
        } else {
          set({ exportError: result.message || 'Failed to download error log. Please try again.' });
          console.error('Failed to download error log:', result.message);
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'An unexpected error occurred while downloading.';
      set({ exportError: errorMessage });
      console.error('Error downloading:', error);
    } finally {
      set({ isExporting: false });
    }
  },

  setSearchTerm: (term) => set({ searchTerm: term }),
  
  clearExportError: () => set({ exportError: null }),

  loadHistory: async () => {
    try {
      set({ isLoadingHistory: true });

      const merchant = isSuperAdmin() 
        ? get().selectedHistoryMerchant 
        : get().selectedMerchant;

      if (isSuperAdmin() && !merchant) {
        set({ historyItems: [] });
        return;
      }

      const merchantId = utils.resolveMerchantId(merchant, isSuperAdmin());
      const result = await massPaymentApiService.listMassPaymentFiles({ merchantId });
      
      set({ historyItems: result.success && result.data ? result.data : [] });
    } catch (error) {
      console.error('Error loading history:', error);
      set({ historyItems: [] });
    } finally {
      set({ isLoadingHistory: false });
    }
  },

  setSelectedHistoryItem: (item) => set({ selectedHistoryItem: item }),

  downloadErrorLog: async (fileId: number) => {
    try {
      const result = await massPaymentApiService.downloadProcessingErrorLog({ fileId });
      
      if (result.success && result.data) {
        utils.downloadFileFromBase64(result.data.contentBase64, `error-log-${fileId}.txt`);
      } else {
        console.error('Failed to download error log:', result.message);
      }
    } catch (error) {
      console.error('Error downloading error log:', error);
    }
  },

  loadMerchants: async () => {
    try {
      const merchantResult = await merchantApiService.getSelectItem();
      set({ merchants: merchantResult.success ? (merchantResult.data || []) : [] });
    } catch (error) {
      console.error('Error loading merchants:', error);
      set({ merchants: [] });
    }
  },

  cancel: () => {
    get().stopPolling();
    set(createResetState());
  },

  startNew: () => {
    get().stopPolling();
    set(createResetState());
  },

  reset: () => {
    get().stopPolling();
    set(initialState);
  },
}));